import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatErr } from "../../lib/api";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { ArrowLeft, Briefcase, MapPin, Calendar, Building2, Check, X, ExternalLink, User, Phone, Mail, ChevronRight, Sparkles, Zap, Image as ImageIcon } from "lucide-react";
import { JobCoverImage } from "../JobCoverImage";
import { JobCoverDialog } from "../company/JobCoverDialog";

const APP_STATUS_CLS = {
  submetida: "bg-slate-100 text-slate-700",
  em_analise: "bg-amber-100 text-amber-700",
  entrevista: "bg-sky-100 text-sky-700",
  aceite: "bg-emerald-100 text-emerald-700",
  rejeitada: "bg-red-100 text-red-700",
};

const STATUS_CLS = {
  active: "bg-emerald-100 text-emerald-700",
  pending: "bg-amber-100 text-amber-700",
  rejected: "bg-red-100 text-red-700",
};

function Stat({ label, value }) {
  return (
    <div className="bg-white rounded-xl border border-black/5 p-4">
      <p className="text-[11px] uppercase tracking-wider font-bold text-slate-500">{label}</p>
      <p className="font-heading font-black tracking-tighter text-2xl text-slate-900 mt-1">{value}</p>
    </div>
  );
}

export function AdminJobDetailSection({ jobId, onBack, onOpenCompany, onOpenApplication }) {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [coverOpen, setCoverOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/jobs/${jobId}`);
      setData(data);
    } catch (e) { toast.error(formatErr(e)); }
  }, [jobId]);

  useEffect(() => { load(); }, [load]);

  const moderate = async (action) => {
    setSaving(true);
    try {
      await api.patch(`/admin/jobs/${jobId}`, { action });
      toast.success(action === "approve" ? "Vaga aprovada." : "Vaga rejeitada.");
      load();
    } catch (e) { toast.error(formatErr(e)); } finally { setSaving(false); }
  };

  const changeAppStatus = async (appId, status) => {
    try {
      await api.patch(`/admin/applications/${appId}`, { status });
      toast.success("Estado atualizado.");
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  if (!data) return <div className="text-slate-500 text-sm">A carregar...</div>;
  const { job, company, applications, boosts, stats } = data;

  return (
    <div className="space-y-6">
      <button type="button" data-testid="admin-job-detail-back" onClick={onBack}
        className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900">
        <ArrowLeft className="w-4 h-4" /> Voltar às vagas
      </button>

      <div className="bg-white rounded-xl border border-black/5 overflow-hidden">
        {/* Cover preview */}
        <div className="relative w-full h-40 sm:h-48 bg-slate-100">
          <JobCoverImage job={job} className="w-full h-full object-cover" />
          <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/40 to-transparent" />
          <Button
            data-testid="admin-job-cover-manage"
            size="sm"
            variant="outline"
            className="absolute bottom-3 right-3 rounded-full bg-white/95 backdrop-blur-sm hover:bg-white"
            onClick={() => setCoverOpen(true)}
          >
            <ImageIcon className="w-3.5 h-3.5 mr-1.5" /> Gerir foto de capa
          </Button>
        </div>
        <div className="p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <h2 data-testid="admin-job-detail-title" className="font-heading font-black tracking-tighter text-2xl text-slate-900 flex items-center gap-2">
              <Briefcase className="w-6 h-6 text-slate-400" />{job.title}
            </h2>
            <div className="flex flex-wrap items-center gap-2 mt-1">
              <Badge className={`${STATUS_CLS[job.status] || "bg-slate-100 text-slate-700"} hover:${STATUS_CLS[job.status]}`}>{job.status}</Badge>
              {job.boost?.tier && <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">{job.boost.tier}</Badge>}
              <span className="text-xs text-slate-500 flex items-center gap-1"><MapPin className="w-3 h-3" />{job.concelho}, {job.district}</span>
              <span className="text-xs text-slate-500 flex items-center gap-1"><Calendar className="w-3 h-3" />{job.created_at ? new Date(job.created_at).toLocaleDateString("pt-PT") : ""}</span>
              <span className="text-xs text-slate-500">{job.regime} · {job.work_mode}</span>
              {job.slug && <a href={`/empregos/${job.slug}`} target="_blank" rel="noreferrer" className="text-xs text-slate-500 hover:text-amber-600 flex items-center gap-1"><ExternalLink className="w-3 h-3" />ver p\u00fablica</a>}
            </div>
            {company && (
              <button type="button" onClick={() => onOpenCompany(company.id)} className="mt-3 inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-amber-600 group">
                <Building2 className="w-4 h-4" />{company.company_name}
                <ChevronRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
              </button>
            )}
          </div>
          {job.status === "pending" && (
            <div className="flex gap-2">
              <Button data-testid="admin-job-detail-approve" size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700" disabled={saving} onClick={() => moderate("approve")}>
                <Check className="w-4 h-4 mr-1.5" />Aprovar
              </Button>
              <Button data-testid="admin-job-detail-reject" size="sm" variant="destructive" className="rounded-full" disabled={saving} onClick={() => moderate("reject")}>
                <X className="w-4 h-4 mr-1.5" />Rejeitar
              </Button>
            </div>
          )}
        </div>
        <p className="text-sm text-slate-600 mt-4 whitespace-pre-line line-clamp-6">{job.description}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Candidaturas" value={stats.total_applications} />
        <Stat label="Com match score" value={stats.with_match_score} />
        <Stat label="Com CV" value={stats.with_cv} />
        <Stat label="Visualiza\u00e7\u00f5es" value={job.views || 0} />
      </div>

      {applications.length > 0 && (
        <div className="bg-white rounded-xl border border-black/5 overflow-hidden">
          <div className="px-5 py-3 border-b border-black/5">
            <p className="font-heading font-bold tracking-tighter text-slate-900">Candidatos ({applications.length})</p>
          </div>
          <ul className="divide-y divide-black/5">
            {applications.map((a) => (
              <li key={a.id} data-testid={`admin-job-app-${a.id}`} className="px-5 py-4 hover:bg-slate-50">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-semibold text-slate-900">{a.candidate_name || "(sem nome)"}</p>
                      {a.match_score != null && (
                        <Badge className="bg-sky-100 text-sky-700 hover:bg-sky-100 text-[10px]"><Sparkles className="w-3 h-3 mr-1" />match {a.match_score}</Badge>
                      )}
                      {a.candidate_has_cv && <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[10px]">com CV</Badge>}
                    </div>
                    <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500 mt-1">
                      {a.candidate_email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{a.candidate_email}</span>}
                      {a.candidate_phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{a.candidate_phone}</span>}
                      {a.candidate_district && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{a.candidate_district}</span>}
                    </div>
                    {a.message && <p className="text-xs text-slate-600 mt-2 italic">“{a.message}”</p>}
                    {a.match_summary && <p className="text-xs text-slate-500 mt-1">{a.match_summary}</p>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <select
                      value={a.status}
                      onChange={(e) => changeAppStatus(a.id, e.target.value)}
                      className={`h-8 rounded-md border border-input bg-white px-2 text-xs font-semibold ${APP_STATUS_CLS[a.status] || ""}`}
                    >
                      <option value="submetida">Submetida</option>
                      <option value="em_analise">Em an\u00e1lise</option>
                      <option value="entrevista">Entrevista</option>
                      <option value="aceite">Aceite</option>
                      <option value="rejeitada">Rejeitada</option>
                    </select>
                    {onOpenApplication && a.candidate_id && (
                      <Button size="sm" variant="outline" className="rounded-full" onClick={() => onOpenApplication(a.candidate_id)}>
                        <User className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {boosts && boosts.length > 0 && (
        <div className="bg-white rounded-xl border border-black/5 p-5">
          <p className="font-heading font-bold tracking-tighter text-slate-900 mb-3 flex items-center gap-2"><Zap className="w-4 h-4 text-amber-500" />Hist\u00f3rico de boosts</p>
          <ul className="space-y-2 text-sm">
            {boosts.map((b) => (
              <li key={b.id} className="flex flex-wrap items-center justify-between border-b border-black/5 pb-2 last:border-0">
                <span className="text-slate-700">{b.label} · <span className="tabular-nums">{((b.amount || 0) / 100).toFixed(0)}\u20ac</span></span>
                <span className="text-xs text-slate-500">Expira {b.expires_at ? new Date(b.expires_at).toLocaleDateString("pt-PT") : "—"}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <JobCoverDialog
        job={job}
        open={coverOpen}
        onOpenChange={setCoverOpen}
        onSaved={() => load()}
        apiPrefix="admin"
      />
    </div>
  );
}
